/* Core & libraries */
import React, {useEffect, useRef, useState} from "react";
import { useTheme } from "react-jss";
import DataTable from 'react-data-table-component';
import Popover from "react-bootstrap/Popover";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import { Link, useNavigate } from "react-router-dom";

/* Helpers, constants, custom methods, internal libraries */
import { nameInitials } from "../../helpers/general";
import { useSpinnersContext } from "../../contexts/spinnersContext";

/* Components */
import Pagination from "./pagination";
import GridSkeleton from "./skeletonLoaderGrid";
import UCButton from "../buttons";
import DynamicImageLoader from "../dynamicImageLoader";
import { ReactComponent as VerticalDotsSvg } from "../../resources/images/dots-vertical.svg";
import { ReactComponent as ChevronRightSvg } from "../../resources/images/chevron-right.svg";
import { ReactComponent as ArrowRightSvg } from "../../resources/images/arrow-right-carousel.svg";
import { ReactComponent as SortIcon } from '../../resources/images/sort-icon.svg';
import FailedEndpointView from "components/failedEndpointView";

/* Styles */
import { gridStyles } from "./styles.js"
import { styles } from "./styles.js"
import classNames from "classnames";

const UCGrid = props => {
  const {
    gridName,
    columns,
    data,
    handlePageChange,
    handleSort,
    actions,
    actionsWidth,
    actionsComponent,
    actionsDots = true,
    setSelectedRow,
    actionCallback,
    spinner,
    customColumns,
    noDataComponent,
    avatarNameLink,
    showHeader,
    persistTableHead,
    disableDeleteBy,
    disableDeleteByValue,
    onRowClickRoute,
    onRowClick,
    showChevronIcon,
    columnCustomWidth,
    columnCustomProps,
    defaultSort,
    gridWrapperClassName,
    gridMobileWrapperClassName,
    hideEditAction,
    gridStyle,
    showSkeleton = true,
  } = props;

  const theme = useTheme();
  const classes = styles({ theme });
  const spinnersContext = useSpinnersContext();
  const navigate = useNavigate();
  const intl = useIntl();

  const [overlayOpen, setOverlayOpen] = useState(false)

  const messages = defineMessages({
    flightInfoTo: {
      id: "ucgrid.flightInfo.to",
    },
    noData: {
      id: "ucgrid.noData"
    }
  });

  const customWidth = {
    avatarName: "250px",
    email: "200px",
    bookingInfo: "360px",
    ...columnCustomWidth,
  }

  const handleLeaveClick = (keyValue) => {
    if (keyValue === overlayOpen) {
      setOverlayOpen(null)
    }
  };

  const handleEnterClick = (key) => {
    setOverlayOpen(key)
  };

  const renderCell = (row, fieldName) => {
    if (customColumns && Object.keys(customColumns).includes(fieldName) ) {
      return (
        customColumns[fieldName](row)
      )
    }

    switch (fieldName) {
      case "avatarName":
        const name = `${row.firstName} ${row.lastName}`;
        const email = row?.email ?? row?.emailAddress ?? '-';

        return (
          <Link to={`${avatarNameLink ? avatarNameLink(row) : row.entityID}`} className={classes.link} state={{ userRowDetails: row }}>
            <div className={classes.nameWrapper}>
              <div className={classes.initialsAvatar}>
                <span>{ nameInitials(name) }</span>
              </div>
              <div className={classes.details}>
                <p>{ name }</p>
                <p>{ email }</p>
              </div>
            </div>
          </Link>
        )

      case "bookingInfo":
        return (
          <div className={classes.nameWrapper} onClick={() => onRowClicked(row)}>
            <div className={classes.planeIcon}>
              <span> <DynamicImageLoader imageName={"flight-plane-square.svg"} /> </span>
            </div>
            <div className={classes.details}>
              <p>{ row.departureAirportCode } <ArrowRightSvg className={classes.arrowIcon} /> { row.arrivalAirportCode }</p>
              <p>{ `${row.departureCity} ${intl.formatMessage(messages.flightInfoTo)} ${row.arrivalCity}` }</p>
              <p>{`${row.marketingAirlineCode} ${row.flightNumber}`}</p>
            </div>
          </div>
        )

      case "actions":
        return (
          <div className={classes.actionsWrapper}>
            {
              actions?.length > 0 && actionsDots &&
                <OverlayTrigger
                  trigger="click"
                  rootClose
                  placement="bottom-end"
                  overlay={renderPopover(row)}
                  onEnter={() => handleEnterClick(row)} onExit={() => handleLeaveClick(row)}
                >
                  <div className={classNames(classes.dotsStyle, {[classes.dotsOpenStyle]: overlayOpen === row })}>
                    <VerticalDotsSvg />
                  </div>
                </OverlayTrigger>
            }
          </div>
        )
      case "actionsComponent":
        return actionsComponent && actionsComponent(row);
      case "chevron":
        return (
          <div onClick={() => onRowClicked(row)} className={classes.link}>
            <ChevronRightSvg />
          </div>
        );
      case "passengerName":
        const passengerName = `${row.firstName} ${row.lastName}`;
        return (
          <div className={classes.details}>
            <p>{ passengerName }</p>
          </div>
        )
      default:
        return (
          <div onClick={() => onRowClicked(row)} className={classes.link}>
            { row[fieldName] }
          </div>
        )
    }
  }

  const renderName = (item, gridName) => {
    if(item.customField) {
      return item.fieldName
    } else return  ["actions", "chevron", "actionsComponent"].includes(item.fieldName) ? "" : <FormattedMessage id={`${gridName}.${item.fieldName}`} />
  }

  const deleteAction = row => {
    document.getElementById("root").click();
    setSelectedRow(row)
    actionCallback("delete", row)
  }
  const updateAction = async (row) => {
    setSelectedRow(row)
    await actionCallback("edit", row)
    document.getElementById("root").click();
  }
  const viewAction = async (row) => {
    setSelectedRow(row)
    await actionCallback("view", row)
    document.getElementById("root").click();
  }
  const assignAction = row => {
    document.getElementById("root").click();
    setSelectedRow(row)
    if(row?.assignedTo) {
      actionCallback("unassign", row)
    } else {
      actionCallback("assign", row)
    }
  }

  const onRowClicked = row => {
    if (onRowClickRoute) {
      navigate(onRowClickRoute(row), { state: { userRowDetails: row } })
    } else {
      onRowClick(row)
    }
  }

  const addDisableClass = (row) => {
    if(disableDeleteBy && disableDeleteByValue){
      return row[disableDeleteBy] === disableDeleteByValue ? classes.disabled :  ""
    } else {
      return ""
    }
  }

  const renderAction = (actionName, row) => {
    switch (actionName) {
      case "delete":
        return (
          <UCButton
            className={`${classes.row} ${classes.delete} ${addDisableClass(row)}`}
            onClick={() => deleteAction(row)}
            key={actionName}
          >
            <FormattedMessage id={`${gridName}.removeAction`} />
          </UCButton>
        )
      case "edit":
        if(hideEditAction) return
        else {
          return (
            <UCButton
              className={`${classes.row} ${addDisableClass(row)}`}
              onClick={() => updateAction(row)}
              key={actionName}
              loadingStateStyling={classes.actionDisable}
              spinner={{
                name: `${gridName}Spinner.editAction`,
                size: 20
              }}
              spinnerClassName={classes.editSpinner}
              text={  <FormattedMessage id={`${gridName}.editAction`} />}
            />
          )
        }
      case "view":
        return (
          <UCButton
            className={`${classes.row} ${addDisableClass(row)}`}
            onClick={() => viewAction(row)}
            key={actionName}
            loadingStateStyling={classes.actionDisable}
            spinner={{
              name: `${gridName}Spinner.viewAction`,
              size: 20
            }}
            spinnerClassName={classes.editSpinner}
            text={  <FormattedMessage id={`${gridName}.viewAction`} />}
          />
        )
      case "assign":
        return (
          <UCButton
            className={`${classes.row} ${addDisableClass(row)}`}
            onClick={() => assignAction(row)}
            key={actionName}
          >
            { row?.assignedTo ?  <FormattedMessage id={`${gridName}.unassignAction`} /> : <FormattedMessage id={`${gridName}.assignAction`} /> }
          </UCButton>
        )
    }
  }

  const renderPopover = (row) => {
    return(
      <Popover className={classes.popoverStyle}>
        <div className={classes.popoverDiv}>
          {
            actions.map(action => (
              renderAction(action, row)
            ))
          }
        </div>
      </Popover>
    )
  }

  let gridColumns = [];
  if (data) {
    if (actionsComponent) {
      columns.actionsComponent = {
        display: true,
        sortable: false,
        fieldName: "actionsComponent",
        //customField: true, // so renderName returns the fieldName (empty string or label)
      }
    }
    if (actions?.length > 0) {
      columns.actions = {
        display: true,
        fieldName: "actions",
        sortable: false
      }
    }
    if (showChevronIcon) {
      columns.chevron = {
        display: true,
        fieldName: "chevron",
        sortable: false,
      }
    }
    if (columns) {
      Object.values(columns).map((item, index) => {
        if (item.display) {
          let data = {
            id: item.fieldName,
            name:  renderName(item, gridName),
            selector: row => renderCell(row, item.fieldName),
            sortable: handleSort && defaultSort && item.sortable,
            minWidth: Object.keys(customWidth).includes(item.fieldName) ? customWidth[item.fieldName] : "110px",
          }
          if (item.fieldName === "actions") {
            data.width = actionsWidth ? actionsWidth : "30px";
            data.allowOverflow = true;
            data.right = true;
            data.style = {
              cursor: "pointer",
              position: "sticky",
              right: "0px",
            }
          }
          if (item.fieldName === "actionsComponent") {
            data.right = true;
          }
          if (item.fieldName === "chevron") {
            data.width = "30px";
            data.allowOverflow = true;
            data.style = {
              cursor: "pointer",
              position: "sticky",
              right: "0px",
              justifyContent: "flex-end",
            }
          }

          columnCustomProps && columnCustomProps.map((column, index) => {
            if (item.fieldName === column.columnName) {
              data.width = column.width;
              data.allowOverflow = column.allowOverflow;
              data.style = column.style
              if(column.tooltip === true) {
                data.name = <div><FormattedMessage id={`${gridName}.${item.fieldName}`} />{column.component}</div>
              }
            }
          })

          gridColumns.push(data)
        }
      })
    }
  }

  let noDataProps = {};
  if (noDataComponent) {
    noDataProps.noDataComponent = noDataComponent;
  } else {
    noDataProps.noDataComponent = intl.formatMessage(messages.noData)
  }

  return (
    <div className={classNames(classes.gridMobileWrapper, gridMobileWrapperClassName)}>
      <div className={classNames(gridWrapperClassName, {
        [classes.gridWrapper]: data?.dataItems.length || showHeader,
        'chevron': showChevronIcon || onRowClickRoute,
        [gridStyle]: gridStyle
      })}>
        {/* (fix for briefly wrongfully showing noDataComponent) - while the grid data is loading, always show the skeleton (even if we have no actual dataItems, BE always returns obj values but with empty list, so the DataTable native props still apply) */}
        {data ? (
          <DataTable
            defaultSortFieldId={defaultSort?.fieldName}
            defaultSortAsc={defaultSort?.asc}
            sortIcon={<SortIcon className={classes.sortIcon}/>}
            progressPending={ showSkeleton && !!spinnersContext[spinner] }
            progressComponent={ showSkeleton && <GridSkeleton gridStyle={gridStyle}/> }
            customStyles={data?.dataItems.length || showHeader ? gridStyles : ""}
            columns={gridColumns}
            data={data?.dataItems}
            pagination
            paginationServer
            // onChangePage={handlePageChange }
            paginationComponent={ pagination => {
              return <Pagination
                pagination={pagination}
                handlePageChange={handlePageChange}
                data={data}
              />
            }}
            onSort={handleSort}
            sortServer
            {...noDataProps}
            persistTableHead={persistTableHead}
            onRowClicked={onRowClicked}
          />
        ) : (
          <>
            {showSkeleton && data !== null ? (
              <GridSkeleton gridStyle={gridStyle} />
            ) : (
              data === null && <FailedEndpointView />
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default UCGrid;
